import { StatusError } from '@xappex/es-apibuilder/status-error';
import { gzip } from 'pako';
import { startRequest, stopRequest } from 'src/api';
import { officeStore, userStore } from 'src/stores';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'HEAD' | 'DELETE';

declare var config: any;

export enum RequestEvents {
  apiRequestStarted = 'API_REQUEST_STARTED',
  apiRequestDone = 'API_RESPONSE_DONE',
}

export async function request(
  method: Method,
  uri: string,
  data: any,
  token: string,
  signal?: AbortSignal,
): Promise<Response> {
  startRequest();
  try {
    const url = `${config.api.serverUrl1}${uri}`;
    const params: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      mode: 'cors',
      signal,
    };

    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      await compressBody(params, data);
    }

    const response = await fetch(url, params);

    if (!response) {
      throw new Error('No response received from server');
    }

    if (response.ok) {
      return response;
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    if (!isJson) {
      const responseText = await response.text();
      const errorText = responseText || `API ${response.status}: ${response.statusText}`;
      console.error(errorText);
      throw new Error(errorText);
    }

    const errorData = await response.json();
    const status = errorData.status || response.status;
    const code = errorData.code || 'unknown';
    const message = errorData.message || '';

    console.error('API Error:', status, code, message);

    if (status === 401) {
      await userStore.signOut(true);
    }

    if (status === 503 && code === 'maintenance_mode') {
      officeStore.maintenanceMode = true;
    }

    throw new StatusError(status, code, message);
  } finally {
    stopRequest();
  }
}

async function compressBody(params: RequestInit, data: any) {
  params.body = JSON.stringify(data);
  if (params.body.length < 1024) {
    return;
  }

  // Maximum size for lambda function is 6291456. Bue in case of binary format base64 is used. So it needs to subtract 4/3
  params.body = await gzip(params.body);
  params.headers = { ...params.headers, 'Content-Encoding': 'gzip' };
}
